/**
 * When the engine refuses a tool's settings, and when the page says so.
 *
 * ── THE DEFECT ─────────────────────────────────────────────────────────────
 * An op the engine refuses ('invalid-op-parameters', severity error) returns
 * the document UNCHANGED. The page treated that like any other result: on
 * /sync-subtitles at 6772448, two points with equal or reversed times showed
 * the unchanged file and Download handed it over as if it were synced. A
 * wrong file that looks right. So a refused run produces no bytes, and the
 * page says why under the options.
 *
 * ── WHEN IT SAYS SO ────────────────────────────────────────────────────────
 * Not on every refused run. A time box starts at 0 and parses at nearly every
 * keystroke ("1" is a millisecond, "00:4" is four seconds), so a sync is
 * refused at almost every step of typing four valid times: after the first
 * box (both targets still 0), and all the way through typing a later time
 * that briefly reads as an earlier one. Saying "wrong" at each of those
 * steps is an error that flashes while the user is still on their way.
 *
 * So the message waits until every time and rate box has been filled in by
 * the user (or by the page, from the file), and the user has stopped typing:
 * a `change` or focus leaving the box. Download is off the whole time; only
 * the sentence waits.
 */

import type { Diagnostic } from '../../../../../contracts/cue-model.ts';
import type { OptionField, OptionsSchema, OptionValues } from './options-schema.ts';

/** True when the engine refused to run an op as configured. */
export function isRefusal(diagnostics: readonly Diagnostic[]): boolean {
  // Severity matters: a cancelled op reports the same code as 'info', and the
  // reflow's "lines left long" note reports it as a 'warning'. Neither is a
  // refusal of what the user asked for.
  return diagnostics.some((d) => d.code === 'invalid-op-parameters' && d.severity === 'error');
}

/**
 * The fields a refusal waits for: every time and rate box.
 *
 * Those are the two kinds whose untouched or half-typed value reads as a real
 * one. A number or a text box that is not filled in is null or empty, which
 * the op reads as "off", so it is never the reason for a refusal mid-entry.
 * Fields inside a repeat row are left out: their values live in the row, not
 * under their own key, so they could never count as filled.
 */
export function awaitedFields(schema: OptionsSchema): readonly string[] {
  const keys: string[] = [];
  const walk = (fields: readonly OptionField[]) => {
    for (const field of fields) {
      if (field.type === 'duration' || field.type === 'framerate') keys.push(field.key);
      if (field.children) walk(field.children);
    }
  };
  for (const group of schema.groups) walk(group.fields);
  return keys;
}

export interface RefusalState {
  /** The latest run has finished and the engine refused it. */
  readonly refused: boolean;
  /** The user is mid-edit in a box: input since the last change or blur. */
  readonly typing: boolean;
  /** From `awaitedFields`. */
  readonly awaited: readonly string[];
  /** Keys the user (or the page, from the file) has entered something in. */
  readonly entered: ReadonlySet<string>;
  /** The current option values: an entered box the user then cleared holds null. */
  readonly values: OptionValues;
}

/** Whether the refusal message is on screen. Download is off whenever `refused`. */
export function refusalShown(state: RefusalState): boolean {
  if (!state.refused || state.typing) return false;
  return state.awaited.every((key) => {
    const value = state.values[key];
    return state.entered.has(key) && value !== null && value !== undefined;
  });
}
